'use client';
import { useState, useEffect } from 'react';
import { getPostsByCategory, getVideos } from '@/lib/api';
import { Story, Video } from '@/lib/types';
import { notFound } from 'next/navigation';
import StoryCard from '@/components/ui/StoryCard';
import VideoCard from '@/components/ui/VideoCard';
import SectionHeader from '@/components/ui/SectionHeader';
import KeyFactsBox from '@/components/ui/KeyFactsBox';
import TimelineModule from '@/components/modules/TimelineModule';
import TagPill from '@/components/ui/TagPill';
import Link from 'next/link';
import { use } from 'react';

const topicMeta: Record<string, {
  name: string;
  summary: string;
  tags: string[];
  facts: string[];
}> = {
  'ukraine-war': {
    name: 'Ukraine War',
    summary: 'Russia launched a full-scale invasion of Ukraine on 24 February 2022. The conflict has caused Europe\'s largest displacement crisis since WWII and reshaped the global security order. Veritas News has reported from the front lines, from Kyiv to the diplomatic corridors of Brussels and Washington.',
    tags: ['Peace Talks', 'Weapons', 'Refugees', 'Kyiv', 'Russia', 'NATO', 'Aid'],
    facts: [
      'Over 6 million Ukrainians have been displaced internationally',
      'More than $200 billion in military aid provided by Western nations',
      'The conflict has caused an estimated $500 billion in economic damage to Ukraine',
      '32 nations participated in the January 2025 Geneva talks',
      'Ukraine has retaken approximately 40% of territory lost in 2022',
    ],
  },
  'climate': {
    name: 'Climate & Environment',
    summary: 'The planet is warming faster than at any point in the past 800,000 years. Veritas News covers the science, policy and human reality of climate change — from record ocean temperatures to the political battles over net-zero commitments.',
    tags: ['Oceans', 'Emissions', 'COP', 'Fossil Fuels', 'Renewables', 'Biodiversity', 'Migration'],
    facts: [
      '2024 was the hottest year on record for the fifth consecutive year',
      'Ocean surface temperatures broke 140-year records in January 2025',
      'More than 80 million people displaced by climate-related events since 2020',
      'Renewable energy now accounts for 35% of global electricity generation',
      '170 nations signed the Nairobi Plastic Treaty in December 2024',
    ],
  },
  'ai': {
    name: 'Artificial Intelligence',
    summary: 'AI is transforming every sector from medicine to journalism. Veritas News tracks the technology, the companies behind it, and the regulatory battles being fought over its future.',
    tags: ['Machine Learning', 'Regulation', 'Big Tech', 'Jobs', 'Safety', 'EU AI Act', 'ChatGPT'],
    facts: [
      'The global AI market is projected to reach $1.8 trillion by 2030',
      'Tech giants committed to EU AI transparency standards in April 2025',
      'AI discovered a potential MRSA antibiotic in 48 hours at MIT',
      'More than 300 million jobs globally may be partially automated by AI by 2035',
      'The EU AI Act is the world\'s first comprehensive AI regulatory framework',
    ],
  },
};

const defaultTopic = {
  name: 'Topic',
  summary: 'Comprehensive coverage of this topic from Veritas News correspondents around the world.',
  tags: ['News', 'Analysis', 'Feature', 'Opinion'],
  facts: ['Coverage from our international correspondents', 'Updated as events develop', 'Analysis from expert contributors'],
};

export default function TopicPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);
  const [activePill, setActivePill] = useState<string | null>(null);
  const [topicStories, setTopicStories] = useState<Story[]>([]);
  const [topicVideos, setTopicVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const posts = await getPostsByCategory(slug);
        setTopicStories(posts);
        
        const videos = await getVideos(slug === 'climate' ? 'Climate' : undefined);
        setTopicVideos(videos);
      } catch (error) {
        console.error('Error loading topic data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug]);

  const meta = topicMeta[slug] || defaultTopic;
  
  if (loading && topicStories.length === 0) {
    return <div className="max-w-[1400px] mx-auto px-5 py-20 text-center">Loading Topic...</div>;
  }

  const allStories = topicStories;
  const leadStory = allStories[0];
  const gridStories = allStories.slice(1, 7);
  const mostRead = [...allStories].sort((a, b) => (b.readCount || 0) - (a.readCount || 0)).slice(0, 5);

  return (
    <div className="max-w-[1400px] mx-auto px-5">

      {/* TOPIC HEADER */}
      <div className="border-b-4 border-[#C8102E] pb-6 mb-8 pt-8">
        <h1 className="font-['Playfair_Display',Georgia,serif] font-black text-[40px] lg:text-[52px] leading-[1.1] mb-4">
          {meta.name}
        </h1>
        <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.6] max-w-[700px] mb-5 text-[#2D2D2D]">
          {meta.summary}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {meta.tags.map(t => (
            <TagPill
              key={t}
              label={t}
              active={activePill === t}
              onClick={() => setActivePill(activePill === t ? null : t)}
            />
          ))}
        </div>
        <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#9A9A9A]">
          {allStories.length} stories · Updated {allStories[0]?.publishedAt || 'Recently'}
        </p>
      </div>

      {/* LEAD STORY */}
      {leadStory && (
        <Link href={`/article/${leadStory.slug}`} className="grid grid-cols-1 md:grid-cols-[45%_55%] gap-0 mb-8 border border-[#D4D4D4] hover:opacity-80 transition-opacity cursor-pointer group block">
          <div 
            className={`${leadStory.imageBg?.startsWith('bg-') ? leadStory.imageBg : 'bg-gray-100'} aspect-video bg-cover bg-center bg-no-repeat`} 
            style={!leadStory.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${leadStory.imageBg})` } : {}}
          />
          <div className="px-8 py-6">
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] block mb-2">{leadStory.publishedAt}</span>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[24px] leading-[1.2] mb-3 group-hover:text-[#C8102E] transition-colors">
              {leadStory.title}
            </h2>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] leading-[1.55] text-[#2D2D2D] mb-4">{leadStory.deck}</p>
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">By {leadStory.author} · {leadStory.readingTime} min read</p>
          </div>
        </Link>
      )}

      {/* LATEST + SIDEBAR */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 mb-10">
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gridStories.map(story => <StoryCard key={story.id} story={story} size="medium" />)}
          </div>
        </div>
        <div className="lg:border-l lg:border-[#D4D4D4] lg:pl-8">
          <KeyFactsBox facts={meta.facts} />
          <div className="mt-6">
            <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Key Figures</h3>
            </div>
            {[
              { name: 'António Guterres', role: 'UN Secretary-General', relevance: 'Leading mediator' },
              { name: 'Volodymyr Zelensky', role: 'Ukrainian President', relevance: 'Negotiating party' },
              { name: 'Josep Borrell', role: 'EU Foreign Policy Chief', relevance: 'Alliance coordinator' },
            ].map((person, i) => (
              <div key={i} className="py-2.5 border-b border-[#D4D4D4]">
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px]">{person.name} <span className="font-normal text-[#6B6B6B]">· {person.role}</span></p>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#9A9A9A] mt-0.5">{person.relevance}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-3">
              <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Background</h3>
            </div>
            {['What started this conflict?', 'A guide to the key players', 'Understanding the diplomatic framework'].map((title, i) => (
              <Link key={i} href="/world" className="block py-2.5 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[13px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{title}</h4>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* TIMELINE */}
      {slug === 'ukraine-war' && (
        <section className="border-t border-[#D4D4D4] pt-8 mb-10">
          <SectionHeader title="Key Moments" />
          <div className="max-w-[700px]">
            <TimelineModule />
          </div>
        </section>
      )}

      {/* EXPLAINERS & ANALYSIS */}
      <section className="bg-[#F5F4F0] py-9 -mx-5 px-5 border-t border-[#D4D4D4] mb-8">
        <SectionHeader title="Analysis & Explainers" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {allStories.filter(s => ['analysis', 'explainer', 'feature'].includes(s.label || '')).slice(0, 3).map(story => (
            <StoryCard key={story.id} story={story} size="medium" />
          ))}
        </div>
      </section>

      {/* RELATED VIDEO */}
      <section className="py-8 border-t border-[#D4D4D4] mb-8">
        <SectionHeader title="Video" moreLink="/video" moreLabel="More →" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {topicVideos.slice(0, 4).map(video => <VideoCard key={video.id} video={video} />)}
        </div>
      </section>

      {/* MOST READ */}
      <section className="py-8 border-t border-[#D4D4D4] mb-10">
        <SectionHeader title="Most Read" moreLink="/latest" moreLabel="All →" />
        <div className="max-w-[600px]">
          {mostRead.map((story, i) => (
            <Link key={story.id} href={`/article/${story.slug}`} className="flex items-start gap-4 py-3.5 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group cursor-pointer">
              <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] text-[#D4D4D4] leading-none w-7 shrink-0 text-right">{i + 1}</span>
              <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[14px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{story.title}</h4>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
